'use client';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { Carousel } from '../ui/carousel';
import { BlogCard } from './blog-card';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CarouselControls } from '../ui/carousel-controls';

const INTERVAL = 5000;

export function BlogsListCarousel({ blogs }: { blogs: Blog[] }) {
  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  const [progress, setProgress] = useState(0);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const [completedIndexes, setCompletedIndexes] = useState<Set<number>>(new Set());

  useEffect(() => {
    setCompletedIndexes(prev => {
      const next = new Set(prev);
      for (let i = 0; i < selectedIndex; i++) next.add(i);
      if (selectedIndex === 0) return new Set();
      return next;
    });

    setProgress(0);
    startTimeRef.current = null;

    if (animationRef.current) cancelAnimationFrame(animationRef.current);

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const next = Math.min((elapsed / INTERVAL) * 100, 100);
      setProgress(next);
      if (next < 100) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [selectedIndex]);

  return (
    <div className="relative w-full pr-5 sm:pr-0">
      <div className="absolute left-4 top-4 z-10 flex w-full items-center justify-start gap-2 sm:left-5 sm:top-5 lg:left-6 lg:top-6">
        {blogs.map((_, index) => {
          const isActive = index === selectedIndex;
          const isCompleted = completedIndexes.has(index);

          return (
            <div key={index} className="h-1 w-10 overflow-hidden rounded-full bg-white/50">
              <div
                className={cn('h-1 rounded-full bg-white', isCompleted && 'transition-none')}
                style={{
                  width: isCompleted ? '100%' : isActive ? `${progress}%` : '0%',
                }}
              />
            </div>
          );
        })}
      </div>
      <Carousel
        intervalLength={INTERVAL}
        enableInterval={true}
        enableLoop={true}
        className={{
          wrapper: 'w-full',
          slide: 'w-full',
          main: 'w-full',
          container: 'w-full',
        }}
        slides={blogs.map(blog => (
          <BlogCard key={blog.slug} isFirst={true} blog={blog} />
        ))}
      />
      <CarouselControls
        className={{
          main: 'absolute bottom-4 right-4 z-10 hidden items-center justify-end gap-2 sm:bottom-5 sm:right-5 sm:flex lg:bottom-6 lg:right-6',
        }}
      />
    </div>
  );
}
